import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { GetIssueUpdateByIdResponseRow } from '@risksmart-app/trpc/types';
import type { GetIssueUpdateByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useIsFeatureVisibleToOrg } from 'src/utils/featureFlags';
import { useTRPC } from 'src/utils/trpc';

const mapTrpcIssueUpdateToGraphQL = (
  trpcData: GetIssueUpdateByIdResponseRow[] | undefined
): GetIssueUpdateByIdQuery => {
  return {
    issue_update: trpcData || [],
  };
};

export const useGetIssueUpdateByIdTRPC = (id?: string, skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  if (!id && !skip) {
    throw new Error('Issue Update ID is required unless skip is true');
  }

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.issueUpdate.getById.queryOptions({
      id: id!,
    }),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcIssueUpdateToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
  };
};
